// src/components/Layout/Header.jsx

import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import styles from "../../styles/styles";
import logo from "../../images/logo.png";
import { categoriesData } from "../../static/data";
import {
  AiOutlineHeart,
  AiOutlineSearch,
  AiOutlineShoppingCart,
  AiOutlineCamera,
  AiOutlineMenu,
} from "react-icons/ai";
import { IoIosArrowDown, IoIosArrowForward } from "react-icons/io";
import { CgProfile } from "react-icons/cg";
import DropDown from "./DropDown";
import Navbar from "./Navbar";
import { useSelector, useDispatch } from "react-redux";
import Cart from "../cart/Cart";
import Wishlist from "../Wishlist/Wishlist";
import { RxCross1 } from "react-icons/rx";
import { server } from "../../server";
import axios from "axios";
import { toast } from "react-toastify";
import { logEvent } from "../../analytics/ga4";
import { setUserPinCodeAction } from "../../redux/actions/user";

const Header = ({ activeHeading }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, user, pinCode, coordinates } = useSelector((state) => state.user);
  const { isSeller } = useSelector((state) => state.seller);
  const { wishlist } = useSelector((state) => state.wishlist);
  const { cart } = useSelector((state) => state.cart);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchData, setSearchData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [active, setActive] = useState(false);
  const [dropDown, setDropDown] = useState(false);
  const [openCart, setOpenCart] = useState(false);
  const [openWishlist, setOpenWishlist] = useState(false);
  const [open, setOpen] = useState(false);
  const searchRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  
  // New state variables for pin code input
  const [pinCodeInput, setPinCodeInput] = useState(pinCode || "");
  const [pinCodeError, setPinCodeError] = useState("");

  const logoutHandler = () => {
    axios
      .get(`${server}/user/logout`, { withCredentials: true })
      .then((res) => {
        toast.success(res.data.message);
        window.location.reload(true);
        navigate("/login");
      })
      .catch((error) => {
        console.log(error.response?.data?.message || "Logout failed");
      });
  };

  useEffect(() => {
    const handleScroll = () => {
      setActive(window.scrollY > 70);
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setSearchData([]); // Close search suggestions
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [searchRef]);

  const handleProductClick = (productId, e) => {
    e.stopPropagation(); // Prevent event bubbling

    setOpen(false); // Close sidebar
    setSearchData([]); // Clear search suggestions
    navigate(`/product/${productId}`); // Navigate to product page
  };

  const handleSearchChange = async (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    setLoading(true);
    setError(null);

    if (term.trim() === "") {
      setSearchData([]);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${server}/product/search?term=${encodeURIComponent(term)}`,
        {
          headers: {
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        const products = Array.isArray(result.products)
          ? result.products
          : [];
        setSearchData(products);
      } else {
        setSearchData([]);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setOpen(false);
    setSearchData([]);
    if (searchTerm.trim()) {
      logEvent("Search", "Text search");
      // If there's a text search term, search based on the text
      navigate(`/search?term=${encodeURIComponent(searchTerm)}`);
    } else if (searchData.length > 0) {
      // If no text is given but image search data is present, navigate with image search results
      navigate(`/search?image=true`, { state: { imageSearchData: searchData } });
    } else {
      // If neither search term nor image data is available, show a message
      console.log("No search term or image data available.");
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append("image", file);

      try {
        setLoading(true);
        logEvent("Search", "Image search");
        const response = await fetch(`${server}/product/image-search`, {
          method: "POST",
          body: formData,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          const products = Array.isArray(result.products)
            ? result.products
            : [];
          navigate(`/search?image=true`, { state: { imageSearchData: products } });
        } else {
          navigate(`/search?image=true`, { state: { imageSearchData: [] } });
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  // New handler for pin code submission
  const handlePinCodeSubmit = async (e) => {
    e.preventDefault();
    setPinCodeError("");

    if (!/^\d{6}$/.test(pinCodeInput)) { // assuming Indian pin codes with 6 digits
      setPinCodeError("Pin code must be 6 digits");
      return;
    }

    try {
      await dispatch(setUserPinCodeAction(pinCodeInput));
    } catch (error) {
      setPinCodeError("Invalid pin code");
    }
  };

  return (
    <>
      {/* Parent Wrapper */}
      <div style={{ position: "relative" }}>
        {/* Ticker */}
        <div
          style={{
            backgroundColor: "#53565b",
            padding: "5px 10px",
            height: "30px",
            width: "100%",
            overflow: "hidden",
            zIndex: 1020,
            boxShadow: "0px 2px 2px -2px rgba(64, 68, 64, 0.2)",
          }}
        >
          <div
            style={{
              display: "inline-block",
              whiteSpace: "nowrap",
              fontSize: "14px",
              color: "#ffffff",
              animation: "ticker 30s linear infinite",
              animationPlayState: isPaused ? "paused" : "running",
            }}
            onMouseEnter={(e) => {
              e.stopPropagation();
              setIsPaused(true);
            }}
            onMouseLeave={(e) => {
              e.stopPropagation();
              setIsPaused(false);
            }}
          >
          For parts not found in search or our product data, our procurement team can find the product for you. Mail your requirement to us at <a href="mailto:sales@hawler.co.in" className="underline">sales@hawler.co.in</a> or whatsapp us at <a href="https://wa.me/918072368475" className="underline">+91 8072368475</a>.
          </div>
        </div>

        {/* Inline Styles for Ticker Animation */}
        <style>
          {`
            @keyframes ticker {
              0% {
                transform: translateX(100%);
              }
              100% {
                transform: translateX(-100%);
              }
            }

            /* Responsive font size for ticker on smaller screens */
            @media (max-width: 768px) {
              .ticker-text {
                font-size: 12px;
              }
            }
          `}
        </style>

        {/* Desktop Header */}
        <div className={`${styles.section}`}>
          <div className="hidden 800px:flex items-center justify-between h-[70px] bg-[#ffffff]">
            {/* Logo and "HAWLER" Text */}
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <img src={logo} alt="Logo" className={styles.logo} />
                {/* Added span for "HAWLER" text */}
                <span className="ml-2 text-lg md:text-xl font-bold text-black">
                  HAWLER
                </span>
              </Link>
            </div>

            {/* Search Container with ref attached */}
            <div className="w-[50%] relative" ref={searchRef}>
              <form onSubmit={handleSearchSubmit} className="w-full relative">
                <input
                  type="text"
                  placeholder="Search Product by name or image"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="h-[40px] w-full px-2 border-[#405c48] border-[2px] rounded-md text-sm"
                />
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  id="image-search-input"
                  onChange={handleImageUpload}
                />
                <label htmlFor="image-search-input">
                  <AiOutlineCamera
                    size={30}
                    className="absolute right-10 top-1.5 cursor-pointer"
                  />
                </label>
                <button type="submit">
                  <AiOutlineSearch
                    size={30}
                    className="absolute right-2 top-1.5 cursor-pointer"
                  />
                </button>

                {loading && (
                  <div className="absolute bg-slate-50 shadow-sm z-[9] p-4">
                    Loading...
                  </div>
                )}
                {error && (
                  <div className="absolute bg-red-100 text-red-600 shadow-sm z-[9] p-4">
                    {error}
                  </div>
                )}

                {Array.isArray(searchData) &&
                  searchData.length > 0 &&
                  !loading &&
                  !error && (
                    <div className="absolute min-h-[30vh] w-[80%] bg-slate-50 shadow-sm z-[9] p-4">
                      {searchData.map((i, index) => (
                        <div
                          key={index}
                          onClick={(e) => handleProductClick(i._id, e)}
                          className="cursor-pointer"
                        >
                          <div className="w-full flex items-start py-3">
                            <img
                              src={`${i.images[0]?.url}`}
                              alt=""
                              className="w-[40px] h-[40px] mr-[10px]"
                            />
                            <h1>{i.name}</h1>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
              </form>
            </div>

            {/* User Authentication, Seller Links, and Pin Code Input */}
            <div className="flex items-center space-x-4">
              {/* Pin Code Input */}
              <form onSubmit={handlePinCodeSubmit} className="flex items-center">
                <input
                  type="text"
                  placeholder="Enter Pin Code"
                  value={pinCodeInput}
                  onChange={(e) => setPinCodeInput(e.target.value)}
                  className="h-[40px] w-24 px-2 border-[#405c48] border-[2px] rounded-md text-sm"
                />
                <button type="submit" className="ml-2 px-3 py-1 bg-green-600 text-white rounded-md text-sm">
                  Set
                </button>
              </form>
              {pinCodeError && (
                <span className="text-red-500 text-sm">{pinCodeError}</span>
              )}
              {pinCode && (
                <span className="text-sm text-gray-700">Pin: {pinCode}</span>
              )}

              {/* User Authentication and Seller Links */}
              <div className={`${styles.button}`}>
                {isAuthenticated ? (
                  <button onClick={logoutHandler} className="text-[#fff] flex items-center">
                    User Logout
                  </button>
                ) : (
                  <Link to="/login">
                    <h1 className="text-[#fff] flex items-center">User Login</h1>
                  </Link>
                )}
              </div>

              <div className={`${styles.button}`}>
                <Link to={`${isSeller ? "/dashboard" : "/shop-login"}`}>
                  <h1 className="text-[#fff] flex items-center">
                    {isSeller ? "Seller Dashboard" : "Seller Login"}{" "}
                    <IoIosArrowForward className="ml-1" />
                  </h1>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop Header Bottom Bar */}
        <div
          className={`${
            active ? "shadow-sm fixed top-0 left-0 z-10" : null
          } transition hidden 800px:flex items-center justify-between w-full bg-[#53565b] h-[70px]`}
        >
          <div className={`${styles.section} relative ${styles.noramlFlex} justify-between`}>
            <div onClick={() => setDropDown(!dropDown)}>
              <div className="relative h-[45px] mt-[5px] w-[270px] hidden 1000px:block ">
                <AiOutlineMenu size={25} className="absolute top-2.5 left-2" />
                <button className="h-[100%] w-full flex justify-between items-center pl-10 bg-white font-sans text-lg font-[500] select-none rounded-md">
                  All Categories
                </button>
                <IoIosArrowDown
                  size={20}
                  className="absolute right-2 top-4 cursor-pointer"
                  onClick={() => setDropDown(!dropDown)}
                />
                {dropDown && (
                  <DropDown
                    categoriesData={categoriesData}
                    setDropDown={setDropDown}
                  />
                )}
              </div>
            </div>
            <div className={`${styles.noramlFlex}`}>
              <Navbar active={activeHeading} />
            </div>
            <div className="flex">
              <div className={`${styles.noramlFlex}`}>
                <div
                  className="relative cursor-pointer mr-[15px]"
                  onClick={() => setOpenWishlist(true)}
                >
                  <AiOutlineHeart size={30} color="rgb(255 255 255 / 83%)" />
                  <span className="absolute right-0 top-0 rounded-full bg-[#3bc177] w-4 h-4 text-white font-mono text-[12px] leading-tight text-center">
                    {wishlist?.length || 0}
                  </span>
                </div>
              </div>
              <div className={`${styles.noramlFlex}`}>
                <div
                  className="relative cursor-pointer mr-[15px]"
                  onClick={() => setOpenCart(true)}
                >
                  <AiOutlineShoppingCart
                    size={30}
                    color="rgb(255 255 255 / 83%)"
                  />
                  <span className="absolute right-0 top-0 rounded-full bg-[#3bc177] w-4 h-4 text-white font-mono text-[12px] leading-tight text-center">
                    {cart?.length || 0}
                  </span>
                </div>
              </div>
              <div className={`${styles.noramlFlex}`}>
                <div className="relative cursor-pointer mr-[15px]">
                  {isAuthenticated ? (
                    <Link to="/profile">
                      <img
                        src={`${user?.avatar?.url}`}
                        className="w-[35px] h-[35px] rounded-full"
                        alt="Profile"
                      />
                    </Link>
                  ) : (
                    <Link to="/login">
                      <CgProfile size={30} color="rgb(255 255 255 / 83%)" />
                    </Link>
                  )}
                </div>
              </div>
              {openCart && <Cart setOpenCart={setOpenCart} />}
              {openWishlist && <Wishlist setOpenWishlist={setOpenWishlist} />}
            </div>
          </div>
        </div>

        {/* Mobile Header */}
        <div
          className={`${
            active ? "shadow-sm fixed top-0 left-0 z-10" : null
          } w-full h-auto bg-[#fff] z-50 top-0 left-0 shadow-sm 800px:hidden`}
        >
          <div className="w-full flex flex-col items-center">
            {/* First Row: Menu, Logo, Cart */}
            <div className="w-full px-4 flex items-center justify-between py-2">
              {/* Menu Icon */}
              <div>
                <AiOutlineMenu
                  size={30}
                  className="ml-4"
                  onClick={() => setOpen(true)}
                />
              </div>

              {/* Logo and "HAWLER" Text */}
              <div className="flex items-center">
                <Link to="/" className="flex items-center">
                  <img src={logo} alt="Logo" className={styles.logoMobile} />
                  {/* Added span for "HAWLER" text with responsive sizing */}
                  <span className="ml-2 text-sm sm:text-lg font-bold text-black">
                    HAWLER
                  </span>
                </Link>
              </div>

              {/* Shopping Cart */}
              <div>
                <div
                  className="relative mr-[20px]"
                  onClick={() => setOpenCart(true)}
                >
                  <AiOutlineShoppingCart size={30} />
                  <span className="absolute right-0 top-0 rounded-full bg-[#3bc177] w-4 h-4 text-white font-mono text-[12px] leading-tight text-center">
                    {cart?.length || 0}
                  </span>
                </div>
              </div>
            </div>

            {/* Second Row: Search Input and Pin Code Input */}
            <div className="w-full px-4 mb-4">
              <div className="relative w-full h-auto flex flex-col">
                {/* Search Bar */}
                <form
                  onSubmit={handleSearchSubmit}
                  className="flex items-center w-full border-[#3957db] border-[2px] rounded-md"
                >
                  <input
                    type="search"
                    placeholder="Search Product by name or image"
                    className="h-[35px] flex-1 px-2 text-xs outline-none"
                    value={searchTerm}
                    onChange={handleSearchChange}
                  />
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    id="image-search-input-mobile"
                    onChange={handleImageUpload}
                  />
                  <label htmlFor="image-search-input-mobile">
                    <AiOutlineCamera
                      size={24}
                      className="cursor-pointer text-gray-600 mx-2"
                    />
                  </label>
                  <button type="submit" className="mr-2">
                    <AiOutlineSearch size={24} className="cursor-pointer text-gray-600" />
                  </button>
                </form>

                {/* **Search Suggestions for Mobile View** */}
                {Array.isArray(searchData) && searchData.length > 0 && !loading && !error && (
                  <div className="absolute w-full bg-slate-50 shadow-md z-10 p-2 mt-12 rounded-md max-h-60 overflow-y-auto">
                    {searchData.map((item, index) => (
                      <div
                        key={index}
                        onClick={(e) => handleProductClick(item._id, e)}
                        className="cursor-pointer flex items-center py-2 hover:bg-gray-100 rounded-md"
                      >
                        <img
                          src={`${item.images[0]?.url}`}
                          alt={item.name}
                          className="w-8 h-8 mr-2 object-cover rounded"
                        />
                        <span className="text-sm text-gray-700">{item.name}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Pin Code Input */}
                <form
                  onSubmit={handlePinCodeSubmit}
                  className="flex items-center justify-between mt-2"
                >
                  <input
                    type="text"
                    placeholder="Enter Pin Code"
                    value={pinCodeInput}
                    onChange={(e) => setPinCodeInput(e.target.value)}
                    className="h-[35px] flex-1 px-2 text-xs border-[#405c48] border-[2px] rounded-md outline-none"
                  />
                  <button
                    type="submit"
                    className="ml-2 px-3 py-1 bg-green-600 text-white rounded-md text-xs"
                  >
                    Set
                  </button>
                </form>

                {/* Pin Code Error */}
                {pinCodeError && (
                  <span className="text-red-500 text-xs mt-1">{pinCodeError}</span>
                )}
              </div>
            </div>

            {/* Conditional Components */}
            {openCart && <Cart setOpenCart={setOpenCart} />}
            {openWishlist && <Wishlist setOpenWishlist={setOpenWishlist} />}
          </div>

          {/* Sidebar Overlay */}
          {open && (
  <div className="fixed w-full bg-[#0000005f] z-20 h-full top-0 left-0">
    <div className="fixed w-[70%] bg-[#fff] h-screen top-0 left-0 z-10 overflow-y-scroll">
      {/* Close button */}
      <RxCross1
        size={20}
        className="ml-4 mt-5 cursor-pointer"
        onClick={() => setOpen(false)}
      />

      <Navbar active={activeHeading} />

      {/* Seller Login Button */}
      <div className={`${styles.button} mt-5 ml-4 !rounded-[4px]`}>
        <Link to={isSeller ? "/dashboard" : "/shop-login"}>
          <h1 className="text-[#fff] flex items-center">
            {isSeller ? "Seller Dashboard" : "Seller Login"}{" "}
            <IoIosArrowForward className="ml-1" />
          </h1>
        </Link>
      </div>

      {/* User Login Button */}
      <div className={`${styles.button} mt-5 ml-4 !rounded-[4px]`}>
        <Link to="/login">
          <h1 className="text-[#fff] flex items-center">
            User Login <IoIosArrowForward className="ml-1" />
          </h1>
        </Link>
      </div>

      {/* Profile Photo & View Profile Button */}
      <div className="flex w-full justify-center mt-4">
        {isAuthenticated ? (
          <div className="flex items-center justify-center">
            <Link to="/profile">
              <img
                src={user.avatar?.url}
                alt="Profile"
                className="w-[60px] h-[60px] rounded-full border-[3px]"
              />
            </Link>
            <Link
              to="/profile"
              className="mt-2 ml-4 px-4 py-2 text-sm font-medium text-white bg-[#21473f] rounded-md shadow-sm hover:bg-[#1c4b3a]"
            >
              View Profile & Settings
            </Link>
          </div>
        ) : null}
      </div>
    </div>
  </div>
)}

        </div>
      </div>
    </>
  );
};

export default Header;
